import {
    Search,
    Mail,
    MapPin,
    ShieldCheck,
    Building2,
    Star,
    CheckCircle2,
    Brain,
    Zap,
    ChevronRight,
    Newspaper,
    Network,
} from "lucide-react";
import { Badge } from "../../components/ui/Badge";

interface Agent {
    id: string;
    name: string;
    tagline: string;
    description: string;
    capabilities: string[];
    icon: React.ReactNode;
    iconBg: string;
    iconColor: string;
    category: string;
    categoryColor: string;
}

const AGENTS: Agent[] = [
    {
        id: "linkedin-finder",
        name: "Profil Scout",
        tagline: "Identifie les dirigeants sur LinkedIn",
        description: "Recherche et associe automatiquement le profil LinkedIn du propriétaire pour chaque établissement détecté dans votre CRM.",
        capabilities: [
            "Recherche par nom et localisation",
            "Validation du profil trouvé",
            "Mise à jour en continu",
        ],
        icon: <Search className="w-5 h-5" />,
        iconBg: "bg-blue-100",
        iconColor: "text-blue-700",
        category: "Enrichissement",
        categoryColor: "bg-blue-50 text-blue-700 border-blue-200",
    },
    {
        id: "email-finder",
        name: "Contact Radar",
        tagline: "Trouve les emails de contact",
        description: "Analyse intelligemment le site web de chaque restaurant pour extraire les emails et identifier celui du propriétaire.",
        capabilities: [
            "Scraping du site web",
            "Détection email propriétaire",
            "Score de validité 0–100",
        ],
        icon: <Mail className="w-5 h-5" />,
        iconBg: "bg-emerald-100",
        iconColor: "text-emerald-700",
        category: "Enrichissement",
        categoryColor: "bg-emerald-50 text-emerald-700 border-emerald-200",
    },
    {
        id: "google-maps",
        name: "Review Monitor",
        tagline: "Enrichit avec les données en ligne",
        description: "Récupère les avis, la note, les horaires et les informations depuis diverses sources en ligne pour chaque établissement de votre base.",
        capabilities: [
            "Note et nombre d'avis",
            "Horaires d'ouverture",
            "Photos et catégories",
        ],
        icon: <MapPin className="w-5 h-5" />,
        iconBg: "bg-orange-100",
        iconColor: "text-orange-700",
        category: "Données",
        categoryColor: "bg-orange-50 text-orange-700 border-orange-200",
    },
    {
        id: "closing-checker",
        name: "Sentinel",
        tagline: "Surveille les fermetures d'établissements",
        description: "Monitore en continu l'activité de chaque restaurant pour détecter les fermetures et maintenir votre base à jour automatiquement.",
        capabilities: [
            "Surveillance 24h/24",
            "Mise à jour automatique du statut",
            "Alertes de fermeture",
        ],
        icon: <ShieldCheck className="w-5 h-5" />,
        iconBg: "bg-violet-100",
        iconColor: "text-violet-700",
        category: "Surveillance",
        categoryColor: "bg-violet-50 text-violet-700 border-violet-200",
    },
    {
        id: "franchise-detector",
        name: "Brand Mapper",
        tagline: "Identifie les réseaux de franchise",
        description: "Analyse chaque établissement pour détecter son appartenance à un réseau de franchise parmi plus de 500 enseignes référencées.",
        capabilities: [
            "Base de 500+ franchises",
            "Détection automatique par nom",
            "Mise à jour en temps réel",
        ],
        icon: <Building2 className="w-5 h-5" />,
        iconBg: "bg-amber-100",
        iconColor: "text-amber-700",
        category: "Analyse",
        categoryColor: "bg-amber-50 text-amber-700 border-amber-200",
    },
    {
        id: "linkedin-scoring",
        name: "Lead Scorer",
        tagline: "Évalue la pertinence des profils",
        description: "Attribue un score de pertinence à chaque profil LinkedIn trouvé pour vous aider à prioriser vos prises de contact.",
        capabilities: [
            "Score de 0 à 100",
            "Analyse du contenu du profil",
            "Priorisation intelligente",
        ],
        icon: <Star className="w-5 h-5" />,
        iconBg: "bg-yellow-100",
        iconColor: "text-yellow-700",
        category: "Analyse",
        categoryColor: "bg-yellow-50 text-yellow-700 border-yellow-200",
    },
    {
        id: "email-scoring",
        name: "Email Qualifier",
        tagline: "Valide et classe les emails trouvés",
        description: "Vérifie la validité de chaque email et détermine automatiquement s'il appartient au propriétaire ou à l'établissement.",
        capabilities: [
            "Validation des emails",
            "Score de confiance 0–100",
            "Distinction propriétaire / entreprise",
        ],
        icon: <CheckCircle2 className="w-5 h-5" />,
        iconBg: "bg-teal-100",
        iconColor: "text-teal-700",
        category: "Analyse",
        categoryColor: "bg-teal-50 text-teal-700 border-teal-200",
    },
    {
        id: "ai-confidence",
        name: "TrustCheck",
        tagline: "Vérifie la qualité des données par IA",
        description: "Un modèle d'intelligence artificielle analyse chaque fiche pour valider la fiabilité des données collectées.",
        capabilities: [
            "Validation par IA",
            "Score de confiance",
            "Filtrage des données erronées",
        ],
        icon: <Brain className="w-5 h-5" />,
        iconBg: "bg-pink-100",
        iconColor: "text-pink-700",
        category: "IA",
        categoryColor: "bg-pink-50 text-pink-700 border-pink-200",
    },
    {
        id: "news-monitor",
        name: "News Tracker",
        tagline: "Surveille l'actualité des établissements",
        description: "Scrute en permanence les actualités, articles de presse et réseaux sociaux pour détecter les événements importants liés à vos prospects.",
        capabilities: [
            "Veille médias et réseaux sociaux",
            "Détection des événements clés",
            "Alertes en temps réel",
        ],
        icon: <Newspaper className="w-5 h-5" />,
        iconBg: "bg-indigo-100",
        iconColor: "text-indigo-700",
        category: "Surveillance",
        categoryColor: "bg-indigo-50 text-indigo-700 border-indigo-200",
    },
    {
        id: "holding-analyzer",
        name: "Multi-Proprio",
        tagline: "Identifie les propriétaires multi-établissements",
        description: "Analyse les structures juridiques et holdings pour détecter automatiquement quand un même propriétaire gère plusieurs restaurants.",
        capabilities: [
            "Analyse des structures de holding",
            "Regroupement par propriétaire",
            "Cartographie des portfolios",
        ],
        icon: <Network className="w-5 h-5" />,
        iconBg: "bg-purple-100",
        iconColor: "text-purple-700",
        category: "Analyse",
        categoryColor: "bg-purple-50 text-purple-700 border-purple-200",
    },
];

export default function AgentsPage() {
    return (
        <div className="min-h-screen bg-canvas font-sans">
            <div className="max-w-6xl mx-auto px-4 md:px-8 py-10">

                {/* Header */}
                <div className="mb-10">
                    <div className="flex items-center gap-2 mb-3">
                        <Zap className="w-4 h-4 text-accent" />
                        <span className="text-xs font-semibold text-ink-faint uppercase tracking-wide">Agents IA</span>
                    </div>
                    <h1 className="font-sans text-2xl font-semibold tracking-tight text-ink mb-3">
                        Vos agents IA au travail
                    </h1>
                    <p className="text-base text-ink-muted max-w-xl">
                        Chaque établissement est automatiquement enrichi par nos agents. Ils travaillent en continu pour vous fournir des données à jour et exploitables.
                    </p>
                </div>

                {/* Stats banner */}
                <div className="bg-surface border border-border rounded-2xl p-5 mb-8 flex items-center gap-8 flex-wrap">
                    <div className="flex items-center gap-2.5">
                        <div className="w-2 h-2 rounded-full bg-success motion-safe:animate-pulse" />
                        <span className="text-sm font-semibold text-ink">Tous les agents actifs</span>
                    </div>
                    <div className="h-4 w-px bg-border" />
                    <div className="text-sm text-ink-muted">
                        <span className="font-semibold text-ink">{AGENTS.length} agents</span> en fonctionnement continu
                    </div>
                    <div className="h-4 w-px bg-border" />
                    <div className="text-sm text-ink-muted">
                        Enrichissement <span className="font-semibold text-ink">24h/24 · 7j/7</span>
                    </div>
                </div>

                {/* Agents grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {AGENTS.map((agent) => (
                        <div
                            key={agent.id}
                            className="bg-surface border border-border rounded-2xl p-5 flex flex-col gap-4 hover:border-border-strong transition-colors duration-150"
                        >
                            {/* Card header */}
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-accent rounded-full flex items-center justify-center text-white flex-shrink-0">
                                        {agent.icon}
                                    </div>
                                    <div>
                                        <p className="text-sm font-semibold text-ink">{agent.name}</p>
                                        <p className="text-xs font-medium text-ink-muted">{agent.tagline}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                    <div className="w-1.5 h-1.5 rounded-full bg-success" />
                                    <span className="text-xs font-semibold text-success">Actif</span>
                                </div>
                            </div>

                            {/* Description */}
                            <p className="text-sm text-ink-muted leading-relaxed">
                                {agent.description}
                            </p>

                            {/* Capabilities */}
                            <ul className="flex flex-col gap-2">
                                {agent.capabilities.map((cap) => (
                                    <li key={cap} className="flex items-center gap-2 text-sm text-ink">
                                        <ChevronRight className="w-3.5 h-3.5 text-ink-faint flex-shrink-0" />
                                        {cap}
                                    </li>
                                ))}
                            </ul>

                            {/* Footer */}
                            <div className="border-t border-border pt-3 mt-auto">
                                <Badge tone="neutral">{agent.category}</Badge>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
